import { useEffect, useState } from "react"
import { motion } from "framer-motion";

import "./ProblemsList.scss";
import CLink from "../CLink";
import { FaCircleCheck, FaRegCircle } from "react-icons/fa6";
import { TbSortAscending, TbSortDescending } from "react-icons/tb";
import { toCapitalCase, toTitleCase } from "../../scripts/Misc";

import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

type ProblemType = {
    id: string,
    title: string,
    difficulty: string,
    tags: [string],
}

type ProblemsListType = {
    problems: [ProblemType],
    perPage: number,
    refresh: () => void,
}

const difficultyColors = {
        "easy": "colorGreen",
        "medium": "colorYellow",
        "hard": "colorRed"
};

export default function ProblemsList(props: ProblemsListType) {

    const [tagsFilter, setTagsFilter] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<'title' | 'difficulty'>('title');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const [problems, setProblems] = useState<ProblemType[]>(props.problems);

    useEffect(() => {
        setProblems(props.problems);
    }, [props.problems]);

    const categories = Array.from(
        new Set(
            props.problems.flatMap(p => p.tags).filter(Boolean)
        )
    );

    const displayedProblems = problems
        .filter(p => !tagsFilter || p.tags.includes(tagsFilter))
        .sort((a, b) => {
            let cmp = 0;
            if (sortBy === 'title') {
                cmp = a.title.localeCompare(b.title);
            } else if (sortBy === 'difficulty') {
                const order = { easy: 0, medium: 1, hard: 2 };
                cmp = order[a.difficulty as keyof typeof order] - order[b.difficulty as keyof typeof order];
            }
            return sortOrder === 'asc' ? cmp : -cmp;
        });

    return (
        <section className="problemSection">

            {/* Filter and sort controls */}
            <div className="problemFilters" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                
                <Select
                    className="tags-select"
                    labelId="tags-label"
                    id="tags-select"
                    value={tagsFilter || ''}
                    label="Tags"
                    size="small"
                    onChange={(e: SelectChangeEvent) => setTagsFilter(e.target.value || null)}
                    notched={false}
                    MenuProps={{
                        PaperProps: {
                            className: 'tags-select-menu'
                        },
                    }}
                >
                    <MenuItem value={''}>All Categories</MenuItem>
                    {categories.map((category) => (
                        <MenuItem key={category} value={category}>{ toTitleCase(category) }</MenuItem>
                    ))}
                </Select>
                
                <Select
                    className="sortby-select"
                    labelId="sortby-label"
                    id="sortby-select"
                    value={sortBy}
                    label="Sort By"
                    size="small"
                    onChange={(e: SelectChangeEvent) => setSortBy(e.target.value as 'title' | 'difficulty')}
                    notched={false}
                    MenuProps={{
                        PaperProps: {
                            className: 'sortby-select-menu'
                        },
                    }}
                >
                    <MenuItem value={"title"}>Sort by Title</MenuItem>
                    <MenuItem value={"difficulty"}>Sort by Difficulty</MenuItem>
                </Select>
                
                <button
                    className="sortOrderButton"
                    title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}>
                    {sortOrder === 'asc' ? <>Asc <TbSortAscending /></> : <>Desc <TbSortDescending /></>}
                </button>
            </div>
            {
                (displayedProblems && displayedProblems.length > 0) && displayedProblems.map((problem, index) => {
                    const isTargetElement = index === displayedProblems.length - 2;

                    return (
                        <motion.div
                            key={index}
                            initial={{ scale: 0.95, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.25, delay: 0.1 }}
                            onViewportEnter={() => {
                                if (isTargetElement) {
                                    props.refresh();
                                }
                            }}
                        >
                            <CLink 
                                to={`/code/${problem.id}`}
                                state={{
                                    from: location.pathname, 
                                }} 
                                target="_blank" 
                                className={`problem ${problem.id}`} 
                            >
                                <div className="left">
                                    <div className={`completed ${problem.difficulty === "completed" ? "completed" : ""}`}>
                                        {problem.difficulty === "completed" ? <FaCircleCheck /> : <FaRegCircle />}
                                    </div>
                                    <div className={`title ${problem.title ? "" : "loading"}`}>{problem.title}</div>
                                </div>
                                <div 
                                    className={`difficulty ${difficultyColors[problem.difficulty as keyof typeof difficultyColors]}`}
                                >
                                    {toCapitalCase(problem.difficulty)}
                                </div>
                            </CLink>
                        </motion.div>
                    )
                })
            }
        </section>
    )

}